// Facet registry loading + the distribution cards, plus the per-metric helpers
// that decide which facets may split a given chart (grain guards live here so
// the metric modules don't each re-derive them).
import { state, $, esc, get, grainOfSrc, scopeQs } from './core'
import { msHtml, msWire } from './multiselect'
import { bars } from './charts'

// Fetch the facet registry + a distribution for every chart/filter facet, so
// dist cards and filters are driven by the registry, not a hardcoded list.
export function loadFacets() {
  return get('/api/facets').then(function (facets) {
    state.facets = facets || [];
    var need = {};
    state.facets.forEach(function (f) {
      var roles = f.roles || [];
      if (roles.indexOf('chart') >= 0 || roles.indexOf('filter') >= 0) need[f.key] = 1;
    });
    state.dist = {};
    return Promise.all(Object.keys(need).map(function (k) {
      return get('/api/distribution?facet=' + encodeURIComponent(k) + scopeQs()).then(function (d) { state.dist[k] = d || []; });
    }));
  });
}

export function renderDists(o) {
  var cards = [];
  state.facets.forEach(function (f) {
    if ((f.roles || []).indexOf('chart') < 0) return;
    var d = state.dist[f.key] || [];
    if (!d.length) return; // skip empty facets (e.g. repo before it resolves)
    cards.push('<div class="card"><h3>' + esc(f.label || f.key) + '</h3>' + bars(d.slice(0, 15), 'value') + '</div>');
  });
  // Non-facet cards (events / tool calls) still come from the overview.
  if (o.outcomes && o.outcomes.length) cards.push('<div class="card"><h3>Outcomes</h3>' + bars(o.outcomes, 'type') + '</div>');
  var tools = (o.topTools || []).map(function (t) { return { value: t.name, count: t.calls }; });
  if (tools.length) cards.push('<div class="card"><h3>Top tools</h3>' + bars(tools, 'value') + '</div>');
  var box = $('#sm-dists'); // distribution cards live under the Sessions tile
  if (box) box.innerHTML = cards.join('');
}

// Facets that can split a session-grain rate/count into series — any facet
// declared as a CHART dimension (we compile each value to a session predicate,
// so counts explode safely). Shared by success-rate + sessions. A facet declares
// its own eligibility via the `chart` role: error_category is `filter`-only, so a
// session's MANY error categories never explode the per-session composite-set
// label here — its clean view is the Ops "Errors by category" widget. Skill stays
// (tool-call grain but a chart dimension), since sessions use few skills.
export function srBreakdownFacets() {
  return state.facets.filter(function (f) {
    return (f.roles || []).indexOf('chart') >= 0;
  });
}

// Cost is usage-grain, so only usage/session-grain facets split it honestly
// (model, repo, use_case, complexity…); tool-call facets (skill) are excluded.
export function spendBreakdownFacets() {
  return state.facets.filter(function (f) {
    var r = f.roles || [];
    if (r.indexOf('chart') < 0 && r.indexOf('filter') < 0) return false;
    return grainOfSrc(f.source) !== 'tool_call';
  });
}

// --- Per-metric filter slicers (success-rate / total-spend / sessions) ---------
// A filter is a session-level predicate (compiled server-side by facetPredicate),
// so the filterable set is BROADER than a measure's *breakdown* set: it includes
// tool-call facets like `skill` even for cost (you can filter spend to "sessions
// that used skill X" though you can't honestly *split* usage-grain cost by skill).
// We offer every filter-role facet with a non-empty distribution; chart-only
// facets (free-text `topics`) are excluded — bad as a dropdown. `repo` is also
// excluded here: it's now the top-level filter-bar scope (multi-select), which
// covers BOTH session and artifact metrics — a duplicate per-section repo filter
// (session-only) would be redundant and confusing.
export function metricFilterFacets() {
  return state.facets.filter(function (f) {
    if (f.key === 'repo') return false;
    return (f.roles || []).indexOf('filter') >= 0 && (state.dist[f.key] || []).length > 0;
  });
}

// Inline filter group for the control row. Only ACTIVE facets render as
// dropdown buttons ("Model · 2 ▾"); everything else collapses behind one
// "+ Filter" popover listing the available facets — pick one and its value
// menu opens. So a button on screen always means "this is filtering the data",
// and the default state is a single quiet button instead of a row of unused
// furniture. OR within a field, AND across fields; absent key = no filter.
// `current` is the section's own {facetKey: value[]} map (per-section, not shared).

// Facets kept visible while still empty (just activated via + Filter). Session-
// lifetime, per section prefix — a full controls rebuild would otherwise hide
// the facet the user just picked before they chose a value.
var mflOpen = {}

export function filterRowHtml(idp, current) {
  var open = (mflOpen[idp] = mflOpen[idp] || {});
  var facets = metricFilterFacets();
  var shown = facets.filter(function (f) { return (current[f.key] || []).length || open[f.key]; });
  var pops = shown.map(function (f) {
    return msHtml(idp + '-fl-' + f.key, f.label || f.key, (current[f.key] || []).length);
  }).join('');
  var avail = facets.filter(function (f) { return shown.indexOf(f) < 0; });
  var add = '';
  if (avail.length) {
    add = '<span class="flt-pop ms-wrap" id="' + idp + '-fl-add-wrap">' +
      '<button type="button" class="flt-pop-btn mfl-add-btn" id="' + idp + '-fl-add-btn">+ Filter</button>' +
      '<div class="flt-menu" id="' + idp + '-fl-add-menu">' +
        avail.map(function (f) {
          return '<button type="button" class="flt-menu-item mfl-add-item" data-key="' + esc(f.key) + '">' +
            '<span class="flt-menu-tx">' + esc(f.label || f.key) + '</span></button>';
        }).join('') +
      '</div></span>';
  }
  return pops + add;
}

// Wire one section's filter popovers + the "+ Filter" adder. `rerender` rebuilds
// the controls when a facet is ACTIVATED (so its button appears); value toggles
// never rerender — the multiselect updates its own badge in place, keeping the
// open menu open mid-pick.
export function wireFacetFilters(idp, box, current, rerender, onChange) {
  void box;
  var open = (mflOpen[idp] = mflOpen[idp] || {});
  metricFilterFacets().forEach(function (f) {
    msWire({
      id: idp + '-fl-' + f.key,
      options: (state.dist[f.key] || [])
        .filter(function (r) { return r.value != null; })
        .map(function (r) { return { value: String(r.value), label: String(r.value), count: r.count }; }),
      selected: (current[f.key] || []).slice(),
      onChange: function (vals) {
        if (vals.length) current[f.key] = vals;
        else { delete current[f.key]; open[f.key] = true; } // keep the button until the next rebuild
        onChange();
      },
    });
  });
  var addBtn = document.getElementById(idp + '-fl-add-btn');
  var addMenu = document.getElementById(idp + '-fl-add-menu');
  if (addBtn && addMenu) {
    addBtn.onclick = function () { addMenu.classList.toggle('on'); };
    Array.prototype.forEach.call(addMenu.querySelectorAll('.mfl-add-item'), function (b) {
      b.onclick = function () {
        var k = b.getAttribute('data-key');
        open[k] = true;
        rerender(); // the facet's button now exists…
        var menu = document.getElementById(idp + '-fl-' + k + '-menu');
        if (menu) menu.classList.add('on'); // …and opens straight into its values
      };
    });
  }
}

// Serialize a section's filter map to query-string params (each leading '&'):
// one repeated param per selected value (?model=a&model=b), matching the
// array-collecting convention the three over-time endpoints use.
export function facetFilterQs(current) {
  var qs = '';
  Object.keys(current || {}).forEach(function (k) {
    (current[k] || []).forEach(function (v) {
      qs += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(v);
    });
  });
  return qs;
}
